import {Navigate, useParams, useSearchParams} from "react-router";
import {useMutation, useQuery} from "@tanstack/react-query";
import LibraryService from "../../../../services/web/library-service.js";
import {useEffect, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {FaPlus} from "react-icons/fa";
import Button from "../../../../components/button/button.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {EditorContent, EditorContext, useEditor} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Typography from "@tiptap/extension-typography";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import ProjectUtils from "../../../../utils/project-utils.js";
import RichTextToolbar from "../../../../components/rich-text/rich-text-toolbar.jsx";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";
import {getLibraryErrorMessage} from "../utils/library-utils.js";
import useHasPermission from "../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../utils/enums/permission-enum.js";
import LibraryDocumentView from "./library-document-view.jsx";

 function LibraryDocumentEditForm() {
    const navigate = navigateHook();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const [name, setName] = useState("");
    const [folderId, setFolderId] = useState(null);
    const [files, setFiles] = useState([]);

    const { data, isLoading } = useQuery({
        queryKey: ["library-sentry-doc", id],
        queryFn: () => LibraryService.getDocById(id),
        enabled: !!id,
    });
    const editor = useEditor({
        immediatelyRender: false,
        editorProps: {
            attributes: {
                autocomplete: "off",
                autocorrect: "off",
                autocapitalize: "off",
            },
        },
        extensions: [
            StarterKit,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            Typography,
        ],
    });

    useEffect(() => {
        if (id) return;

        const parentId = searchParams.get("parentId");

        if (parentId) {
            setFolderId(Number(parentId));
        }
    }, [id, searchParams]);

    useEffect(() => {
        if (!data) return;
        if (!editor) return;

        setName(data.name ?? "");
        setFolderId(data.parentId ?? null);
        setFiles(data.attaches ?? []);
        editor.commands.setContent(data.description ?? "");
    }, [data, editor]);

    const MAX_FILE_SIZE = 20 * 1024 * 1024;

    const saveMutation = useMutation({
        mutationFn: async (formData) => {
            return id
                ? LibraryService.updateDoc(id, formData)
                : LibraryService.createDoc(formData);
        },
        onSuccess: (response) => {
            ProjectUtils.success(!id);
            if (id) return;

            const savedDocument = response?.data ?? response;
            const newId = savedDocument ?? savedDocument?.libraryItemId;
            navigate(`/library/document/form/${newId}`);
        },
        onError: (error) => {
            console.error(error);
            const message = getLibraryErrorMessage(error);
            ProjectUtils.swalErrorMessage("", message, "Ok");
        },
    });

    const handleSave = (event) => {
        event.preventDefault();

        const formData = new FormData();

        formData.append("name", name);
        formData.append("parentId", folderId ?? "");
        formData.append("description", editor.getHTML());
        formData.append("attaches", JSON.stringify(files.filter(file => file.attachId && !file.new)));
        files.filter(file => file.new).forEach(file => formData.append("files", file));

        saveMutation.mutate(formData);
    }

    const resetForm = ({ keeperFolder = false } = {}) => {
        setName("");
        setFiles([]);
        editor.commands.clearContent(true);

        if (!keeperFolder) {
            setFolderId(null);
        }
    }

    const newDoc = () => {
        const currentParentId = folderId;
        resetForm({ keeperFolder: true });
        navigate(`/library/document/form?parentId=${currentParentId}`);
    }

    const editorClassName = [
        "w-full rounded-md bg-white",
        "focus:outline-none",
        "[&_.ProseMirror]:outline-none",
        //tamanho
        "[&_.ProseMirror]:min-h-[384px]",
        "[&_.ProseMirror]:max-h-[446px]",
        "[&_.ProseMirror]:overflow-auto",
        "[&_.ProseMirror]:py-4 [&_.ProseMirror]:px-6",
        //lista
        "[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6 [&_.ProseMirror_ul]:my-2",
        "[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_ol]:my-2",
        "[&_.ProseMirror_li]:my-1",
        //scroll
        "[&_.ProseMirror::-webkit-scrollbar]:block",
        "[&_.ProseMirror::-webkit-scrollbar]:w-2",
        "[&_.ProseMirror::-webkit-scrollbar-track]:bg-transparent",
        "[&_.ProseMirror::-webkit-scrollbar-thumb]:rounded",
        "[&_.ProseMirror::-webkit-scrollbar-thumb]:bg-zinc-200",
        "[&_.ProseMirror]:[scrollbar-width:thin]",
        "[&_.ProseMirror]:[scrollbar-color:rgb(228_228_231)_transparent]",
        //text
        "[&_.ProseMirror]:leading-9",
        "[&_.ProseMirror]:text-zinc-700",
        "[&_.ProseMirror]:!text-xs",
        "[&_.ProseMirror_p]:!text-xs",
        //link
        "[&_.ProseMirror_a:hover]:cursor-pointer",
        "[&_.ProseMirror_a:hover]:text-blue-500",
    ].join(" ");

    // const selectedFolder = foldersOptions.find((option) => option.value === folderId) ?? null;

    return (
        <Template isLoading={isLoading || saveMutation.isPending}>
            <Template.Header>
                <Template.Title>Sentry Doc</Template.Title>
                {id && (
                    <Button
                        target="primary-outline"
                        onClick={newDoc}
                    >
                        <FaPlus /> Adicionar Sentry Doc
                    </Button>
                )}
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col h-full min-h-0">
                <Card>
                    <Card.Header>
                        <Card.Title>Dados</Card.Title>
                    </Card.Header>
                    <Card.Body className="space-y-4">
                        <InputForm
                            label="Nome *"
                            id="name"
                            value={name}
                            classNameWrapper="flex-1"
                            onChange={(e) => setName(e.target.value)}
                        />
                        <div>
                            <EditorContext.Provider value={{ editor }}>
                                <RichTextToolbar
                                    wrapperClass="!border !border-zinc-200 !rounded-t-md !rounded-b-none !bg-white !shadow-none"
                                />
                                <div className="rounded-b-md border-t-0 border border-zinc-200">
                                    <EditorContent editor={editor} className={editorClassName} />
                                </div>
                            </EditorContext.Provider>
                        </div>
                    </Card.Body>
                </Card>
                <Dropzone
                    title="Anexos"
                    attaches={files}
                    setAttaches={setFiles}
                />
                <Card>
                    <Card.Body>
                        <div className="flex justify-between">
                            <Button
                                target="primary-outline"
                                onClick={() => navigate(`/library?folderId=${folderId}`)}
                            >
                                Voltar
                            </Button>
                            <div className="flex gap-2">
                                <Button

                                    target="primary"
                                    onClick={handleSave}
                                 actionFeedbackType='save'>
                                    Salvar
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}

export default function LibraryDocumentForm() {
    const { id } = useParams();
    const hasPermission = useHasPermission();
    const canEditDocument = hasPermission(PermissionEnum.CONFIGURATION) || hasPermission(PermissionEnum.LIBRARY_MANAGE);

    if (!canEditDocument && !id) {
        return <Navigate to="/library" replace />;
    }

    return canEditDocument ? <LibraryDocumentEditForm /> : <LibraryDocumentView />;
}